import { useEffect, useRef, useState } from "react";
import "./intro-gate-style.scss";
import videoUrl from "../../../utils/video-url";

//—— 開場動畫 ——
//
//  流程：進站 → 播第一支影片 → 停格、「進入」淡入 → 按下 → 播第二支影片
//        → 播完直接淡出進首頁。
//
//  只有第一支播完會停下來等使用者：「進入」是這段開場唯一要使用者做的決定，
//  第二支之後不再攔一次 —— 人已經表達過要進來了，再問一次只是多一道門。
//
//  兩支影片「同時掛在 DOM 上」而不是播完才換掉：
//  第二支從一開始就 preload，等真的要播時已經在快取裡，
//  切換只是 opacity 交叉淡入，不會中間黑一下或轉圈。
//
//  關於聲音 —— 預設就是開著的，但瀏覽器有最終決定權：
//    進站時先直接用「有聲音」去播。多數情況下 Chrome 會放行
//    （使用者常造訪、或曾在本站播過媒體），Safari 則看網站設定。
//    真的被擋下來（play() 回 NotAllowedError）才退回靜音續播，
//    並在右上角露出「開啟聲音」—— 使用者點一下就有聲音，
//    因為那一下點擊本身就是瀏覽器要的授權。
//    第二支是按下「進入」才播，那一刻必定有授權，所以一定帶聲音。
//
//    刻意不做靜音鈕：聲音是這段開場的一部分，
//    唯一會靜音的情況是瀏覽器強制的，而那時該給的是「開啟」而不是「關閉」。
//
//  影片檔放在 public/videos/ 而不是 src/：
//  放 src 會被 webpack 打包進 build 的雜湊檔名裡，換一支影片就要重編譯；
//  放 public 是直接覆蓋檔案就好，也不會拖慢首次 JS 下載。
const SRC_FIRST = videoUrl("walking.mp4");
const SRC_SECOND = videoUrl("opening.mp4");

//第一支影片播到第幾秒才給「略過動畫」。
//  一開場就給等於還沒開始就先請人別看；三秒足夠讓人看出這是一段影片，
//  也知道自己可以不看完。
//
//  比的是影片的 currentTime 而不是牆上時鐘：自動播放被擋、或影片還在載的時候
//  計時器照樣會走，那會在畫面還是黑的時候就冒出一顆「略過」。
const SKIP_AFTER_S = 3;

//離場淡出的時間，要和 scss 裡 .intro--leaving 的 transition 對齊
const LEAVE_MS = 700;

//—— 第二支影片上的題詩 ——
//
//  直書、由右往左五行，整首一起淡進來。
//
//  刻意不做逐字浮現：那需要 49 個常駐節點各自跑 opacity 過場，
//  在真機 iOS 上會因為合成圖層被回收而卡在半透明（見 ios-video-overlay 那次）。
//  整首一起淡入只有一個元素在動，而且是「新掛上來的節點 + animation」——
//  和白夜台詞同一套寫法，那套在真機上是驗證過的。
//  斷句剛好落在原本的標點上，不是硬切的 ——
//  每一行都是一個完整的語意單位，長度也拉平到 9~11 字。
//  直書時「行」是縱向的，所以行變短＝整首的高度變矮，
//  底下的煙霧與門樓才露得出來。
const VERSES = [
  "燈昏茶冷，天機已定；",
  "紅絲纏骨，命格由誰？",
  "一卷竹簡寫盡前生，",
  "半盞青燈照破天機。",
  "入此閣者，無秘密可藏。",
];

//題詩在第二支影片播到第幾秒才淡進來。
//
//  時間點是量出來的：把 opening.mp4 逐格比對，0.8~2.8 秒是推開木門的大動作
//  （畫面平均亮度從 51.9 一路掉到 33.4），3.2 秒之後變化量就穩定在 5 上下，
//  也就是只剩煙霧在緩緩飄。
//
//  取 2.4 而不是等運鏡完全停（2.8~3.2）：那時煙霧已經很明顯，門框也快出鏡，
//  詩跟著最後一段推鏡一起浮起來，比「等畫面定住才貼上去」自然。
//  再往前就會壓在還在開門的畫面上。
//
//  比的是影片的 currentTime 而不是牆上時鐘 —— 理由同 SKIP_AFTER_S。
const POEM_AT_S = 2.4;

const IntroGate = ({ onFinish, enterTxt = "進入" }) => {
  //first（播第一支）→ asking（停格＋「進入」）→ second（播第二支）
  //  → leaving（淡出）
  const [phase, setPhase] = useState("first");

  //預設開聲音。只有在瀏覽器擋下「有聲自動播放」時才會被迫轉靜音
  const [muted, setMuted] = useState(false);

  //被擋下時才露出「開啟聲音」，平常不佔畫面
  const [soundBlocked, setSoundBlocked] = useState(false);

  //題詩該現身了沒。由第二支影片的播放位置決定（見 POEM_AT_S）。
  const [poemIn, setPoemIn] = useState(false);

  //第一支播夠久了沒 —— 夠久才給「略過動畫」
  const [canSkip, setCanSkip] = useState(false);

  //iOS 低耗電模式下連靜音影片都會被擋。擋到時改成「點一下開始」，
  //不然使用者會對著一張靜止的黑畫面等一輩子。
  const [needsTap, setNeedsTap] = useState(false);

  const firstRef = useRef(null);
  const secondRef = useRef(null);

  //淡出計時器要能在元件被提早移除時取消，否則會對已卸載的元件呼叫 onFinish
  const leaveTimer = useRef(null);

  //—— 開場期間鎖住整頁的捲動 ——
  //
  //  底下的首頁在開場播放時就已經渲染好了（刻意的：播完才不用再等內容）。
  //  它有兩三千 px 高，而沒有任何東西擋著文件捲動 ——
  //  .intro 自己是 position: fixed 不會跟著動，但頁面會在它背後滑。
  //
  //  在手機上那就是：影片停在原地、背景在捲，放開手指時人已經滑到半路，
  //  動畫一淡出就落在頁面中段而不是最上面。
  //  iOS 還會因為捲動而收合網址列，可視高度一變，滿版的影片就跟著跳一下。
  //
  //  卸載時還原「原本的值」而不是直接清成空字串 —— 直接清會踩掉別人設的樣式。
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  //進站就播第一支 —— 先用「有聲音」試，這是我們要的預設
  useEffect(() => {
    const video = firstRef.current;
    if (!video) return;

    video.muted = false;
    const played = video.play();
    if (!played?.catch) return;

    played.catch(() => {
      //被瀏覽器擋下（NotAllowedError）：退回靜音續播，畫面不能因此停住，
      //同時把「開啟聲音」露出來讓使用者一鍵要回聲音
      video.muted = true;
      setMuted(true);
      setSoundBlocked(true);

      const retried = video.play();
      if (retried?.catch) retried.catch(() => setNeedsTap(true));
    });
  }, []);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  //影片檔缺失或解碼失敗時不能把人鎖在這一頁 —— 直接推進到下一階段。
  //  開場動畫再重要，也不該是「進不了網站」的理由。
  //略過動畫：直接跳到第一支的停格點，和「乖乖看完」看到的畫面一樣。
  //
  //  要把播放頭移到最後再停住，不能只是暫停 ——
  //  停在半路的那一格是使用者沒走完的地方，看起來像卡住而不像「到了」。
  //  這一下有可能順手觸發 ended（它也會設成 asking），重複設定同一個值無害。
  const handleSkipFirst = () => {
    const video = firstRef.current;
    if (video) {
      if (Number.isFinite(video.duration)) video.currentTime = video.duration;
      video.pause();
    }
    setPhase("asking");
  };

  const handleFirstError = () => setPhase("asking");

  const leave = () => {
    setPhase("leaving");
    leaveTimer.current = setTimeout(onFinish, LEAVE_MS);
  };

  //「開啟聲音」：兩支一起解除靜音，切到第二支時才不會又變回去。
  //  這一下點擊就是瀏覽器要的授權，所以解除之後不會再被擋。
  const enableSound = () => {
    setMuted(false);
    setSoundBlocked(false);

    if (firstRef.current) firstRef.current.muted = false;
    if (secondRef.current) secondRef.current.muted = false;
  };

  //按下按鈕：直接在點擊事件裡呼叫 play()，不要等 state 更新後才播 ——
  //  瀏覽器認的是「這次點擊」的授權，繞一圈到 effect 裡有可能就不算了。
  const handleOpen = () => {
    setPhase("second");

    const video = secondRef.current;
    if (!video) return leave();

    //按下「進入」就是一次明確的使用者授權，這裡一律帶聲音播
    video.muted = false;
    setMuted(false);
    setSoundBlocked(false);

    const played = video.play();
    if (played?.catch) {
      //理論上不會走到這裡（已有點擊授權），但真的失敗時寧可靜音也要播完，
      //連靜音都播不了才放棄、直接進首頁
      played.catch(() => {
        video.muted = true;
        setMuted(true);
        const retried = video.play();
        if (retried?.catch) retried.catch(leave);
      });
    }
  };

  //自動播放被擋時，點畫面任一處重新開始
  const handleTapToStart = () => {
    const video = firstRef.current;
    if (!video) return;

    setNeedsTap(false);
    const played = video.play();
    if (played?.catch) played.catch(() => setPhase("asking"));
  };

  //leaving 也要算進來，否則第二支會在淡出的過程中先被藏起來、露出第一支的尾格
  const showingSecond = phase === "second" || phase === "leaving";

  //唯一的停格點：第一支播完，等使用者按「進入」
  const showGate = phase === "asking";

  return (
    <div
      className={`intro${phase === "leaving" ? " intro--leaving" : ""}`}
      role="dialog"
      aria-label="開場動畫"
    >
      {/* 影片舞台限制在 app 欄寬內，和站內其他頁面同一條欄位 ——
          桌機上不會忽然變成滿版影片、播完又縮回窄欄。
          影片若是橫式而不想被裁切，把 scss 裡的 object-fit 改成 contain。 */}
      <div
        className="intro__stage"
        onClick={needsTap ? handleTapToStart : undefined}
      >
        <video
          ref={firstRef}
          className={`intro__video${showingSecond ? " is-hidden" : ""}`}
          src={SRC_FIRST}
          muted={muted}
          playsInline
          preload="auto"
          //播夠三秒就把「略過動畫」放出來（見 SKIP_AFTER_S）。
          //  timeupdate 一秒觸發四次左右，比自己開計時器準 ——
          //  它量的是「影片真的播了多久」，不是「頁面開了多久」。
          onTimeUpdate={(e) => {
            if (!canSkip && e.currentTarget.currentTime >= SKIP_AFTER_S) {
              setCanSkip(true);
            }
          }}
          onEnded={() => setPhase("asking")}
          onError={handleFirstError}
        />

        <video
          ref={secondRef}
          className={`intro__video${showingSecond ? "" : " is-hidden"}`}
          src={SRC_SECOND}
          muted={muted}
          playsInline
          preload="auto"
          //播到煙霧起來的時間點就把題詩放出來（見 POEM_AT_S）
          onTimeUpdate={(e) => {
            if (!poemIn && e.currentTarget.currentTime >= POEM_AT_S) {
              setPoemIn(true);
            }
          }}
          onEnded={leave}
          onError={leave}
        />

        {/* 題詩。煙霧起來之後才掛上來，掛上的那一刻整首一起淡進來（見 scss）。
            leaving 也留著，否則離場時詩會比影片先不見。

            用「時間到了才掛節點」而不是「一直掛著、改 class 淡入」：
            前者每次都是全新的節點，一定會被繪製；後者在真機 iOS 上會踩到
            合成圖層被回收卻沒重繪的問題（見 ios-video-overlay）。

            aria-hidden：這是氣氛用的題詞，讀屏逐字念出來只是噪音。 */}
        {showingSecond && poemIn && (
          <div className="intro__poem" aria-hidden="true">
            <div className="intro__poem-inner">
              {VERSES.map((line) => (
                <p className="intro__verse" key={line}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* 只有在瀏覽器擋掉有聲自動播放時才出現。
            自動播放被擋時整層都是「點一下開始」，那時這顆就別擋路了 */}
        {soundBlocked && !needsTap && (
          <button type="button" className="intro__sound" onClick={enableSound}>
            <span className="intro__sound-icon" aria-hidden="true">
              🔇
            </span>
            開啟聲音
          </button>
        )}

        {needsTap && (
          <div className="intro__tap">
            <span className="intro__tap-text">點一下開始</span>
          </div>
        )}

        {/* 略過動畫。只在第一支還在播、而且已經播夠久時出現。
            needsTap 時不給 —— 那時整層的意思是「點一下開始」，
            再放一顆按鈕會讓人不知道該點哪裡。 */}
        {phase === "first" && canSkip && !needsTap && (
          <div className="intro__skip">
            <button
              type="button"
              className="intro__skip-btn"
              onClick={handleSkipFirst}
            >
              略過動畫
            </button>
          </div>
        )}

        {/* 只在停格點出現：第一支影片停在最後一格，這層才淡入 */}
        {showGate && (
          <div className="intro__ask">
            <button type="button" className="intro__btn" onClick={handleOpen}>
              {enterTxt}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntroGate;
